<template>
  <div class="flex-lg-1 h-screen overflow-y-lg-auto bg-surface-secondary">
    <vue-element-loading :active="loading" :is-full-screen="true" spinner="ring" color="#5c60f5" />

    <header class="bg-white border-bottom">
      <div class="container-fluid py-6">
        <div class="row align-items-center">
          <div class="col-md-6">
            <h1 class="h2 ls-tight mb-1">Analysis Jobs</h1>
            <p class="text-sm text-muted">Monitor cluster execution status and retrieve genomic calculation results.</p>
          </div>
          <div class="col-md-6 text-md-end">
            <div class="hstack gap-2 justify-content-md-end mt-4 mt-md-0">
              <button class="btn btn-sm btn-neutral" @click="init(currentPage, pageSize)">
                <i class="bi bi-arrow-clockwise me-2"></i>Refresh List
              </button>
            </div>
          </div>
        </div>

        <div class="row g-4 mt-2">
          <div class="col-xl-3 col-sm-6">
            <div class="card shadow-none border">
              <div class="card-body py-4">
                <div class="d-flex align-items-center">
                  <div class="flex-fill">
                    <span class="text-2xs font-bold text-muted text-uppercase mb-1 d-block">Total Tasks</span>
                    <span class="h3 font-black">{{ total }}</span>
                  </div>
                  <div class="icon icon-shape bg-soft-primary text-primary rounded-circle"><i class="bi bi-stack"></i></div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-3 col-sm-6">
            <div class="card shadow-none border">
              <div class="card-body py-4">
                <div class="d-flex align-items-center">
                  <div class="flex-fill">
                    <span class="text-2xs font-bold text-muted text-uppercase mb-1 d-block">Running</span>
                    <span class="h3 font-black text-warning">{{ runningCount }}</span>
                  </div>
                  <div class="icon icon-shape bg-soft-warning text-warning rounded-circle" :class="{'pulse-warning': runningCount > 0}"><i class="bi bi-cpu"></i></div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-3 col-sm-6">
            <div class="card shadow-none border">
              <div class="card-body py-4">
                <div class="d-flex align-items-center">
                  <div class="flex-fill">
                    <span class="text-2xs font-bold text-muted text-uppercase mb-1 d-block">Completed</span>
                    <span class="h3 font-black text-success">{{ finishedCount }}</span>
                  </div>
                  <div class="icon icon-shape bg-soft-success text-success rounded-circle"><i class="bi bi-check2-all"></i></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="py-6">
      <div class="container-fluid">
        <div class="card shadow-sm">
          <div class="card-header d-flex align-items-center border-bottom">
            <h5 class="mb-0">Job Queue</h5>
            <div class="ms-auto">
              <div class="input-group input-group-sm input-group-inline">
                <span class="input-group-text"><i class="bi bi-search"></i></span>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search job name..."
                  v-model.trim="keyword"
                  @keyup.enter="init(1, pageSize)"
                />
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover table-nowrap align-middle">
              <thead class="bg-surface-tertiary">
                <tr>
                  <th scope="col" class="py-4 text-2xs text-uppercase font-bold">Job Identifier</th>
                  <th scope="col" class="py-4 text-2xs text-uppercase font-bold">Pipeline Methods</th>
                  <th scope="col" class="py-4 text-2xs text-uppercase font-bold">Submission Time</th>
                  <th scope="col" class="py-4 text-2xs text-uppercase font-bold">Finished Time</th>
                  <th scope="col" class="py-4 text-2xs text-uppercase font-bold">Execution Status</th>
                  <th scope="col" class="py-4 text-2xs text-uppercase font-bold">Duration</th>
                  <th scope="col" class="py-4 text-2xs text-uppercase font-bold text-end pe-6">Actions</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="job in files.fileList"
                  :key="job.uuid"
                  @click="setEnterDetail(job)"
                  class="cursor-pointer"
                >
                  <td class="ps-6">
                    <div class="d-flex align-items-center">
                      <div class="avatar avatar-xs bg-soft-primary text-primary rounded-circle me-3">
                        <i class="bi bi-file-earmark-medical"></i>
                      </div>
                      <span class="font-bold text-dark">{{ job.name }}</span>
                    </div>
                  </td>

                  <td>
                    <div class="hstack gap-1 flex-wrap" style="max-width: 200px;">
                      <span
                        v-for="tag in (job.algorithmsName || '').split(',').filter(Boolean)"
                        :key="tag"
                        class="badge bg-soft-primary text-primary border-0 rounded-pill px-2"
                        style="font-size: 10px;"
                      >{{ tag }}</span>
                    </div>
                  </td>

                  <td class="text-xs text-muted">{{ job.uploadDate }}</td>
                  <td class="text-xs text-muted">
  {{ job.finishedAt ? new Date(job.finishedAt).toLocaleString() : '--' }}
</td>
                  <td>
                    <span :class="['badge badge-lg badge-dot border-0 p-0', job.status === 'Started' ? 'pulse-active' : '']">
                      <i :class="job.colorClass" class="me-2"></i>
                      <span class="text-dark font-bold">{{ job.status }}</span>
                    </span>
                  </td>

                  <td>
  <span
  v-if="job.durationText"
  :class="[
    'badge border rounded-pill',
    job.status === 'Started'
      ? 'bg-soft-primary text-primary pulse-color'
      : 'bg-neutral text-dark'
  ]"
>
  {{ job.durationText }}
</span>
</td>

                  <td class="text-end pe-6">
                    <div class="hstack gap-2 justify-content-end">
                      <button
                        v-if="job.status === 'Finished'"
                        class="btn btn-xs btn-primary shadow-none rounded-pill"
                        @click.stop="downloadResult(job.uuid)"
                      >
                        <i class="bi bi-download me-1"></i> Result
                      </button>

                      <button
                        v-if="job.status === 'Failed'"
                        class="btn btn-xs btn-soft-danger border-0 rounded-pill"
                        @click.stop="openLogs(job.uuid)"
                      >
                        <i class="bi bi-bug me-1"></i> Logs
                      </button>

                      <!-- <button
                        v-if="['Submitted', 'Started'].includes(job.status)"
                        class="btn btn-xs btn-neutral text-danger border-0 rounded-pill"
                        @click.stop="abortRunner(job.uuid)"
                      >
                        <i class="bi bi-stop-circle me-1"></i> Stop
                      </button> -->

                      <button
                        v-if="['Finished', 'Failed', 'Stopped'].includes(job.status)"
                        class="btn btn-xs btn-square btn-neutral border-0 text-danger-hover rounded-circle"
                        @click.stop="deleteRunner(job.uuid)"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="!files.fileList.length && !loading">
                  <td colspan="6" class="py-10 text-center text-muted">
                    <i class="bi bi-inbox fs-1 d-block mb-3"></i>
                    No jobs found in your history.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-footer border-0 py-4">
            <div class="d-flex justify-content-between align-items-center">
              <span class="text-xs text-muted">Showing {{ files.fileList.length }} items per page</span>
              <el-pagination
                @current-change="changePage"
                @size-change="handleSizeChange"
                :current-page="currentPage"
                :background="true"
                layout="prev, pager, next"
                :total="total"
                :page-size="pageSize"
              />
            </div>
          </div>
        </div>
      </div>
    </main>

    <modal name="as-modal" :scrollable="true" height="auto" :width="600" classes="rounded-4 shadow-lg overflow-hidden">
      <div class="p-0">
        <div class="px-8 py-6 bg-surface-tertiary border-bottom d-flex align-items-center">
          <h5 class="mb-0">Job Configuration Detail</h5>
          <button class="btn-close ms-auto shadow-none" @click="hideModal"></button>
        </div>

        <div class="p-8">
          <div class="mb-8">
            <label class="text-2xs text-uppercase font-bold text-muted mb-3 d-block tracking-wider">Data Sources</label>
            <div
              v-for="(item, i) in (showDetail.gwasAndLDFilenames || [])"
              :key="100 + i"
              class="p-3 bg-light rounded-3 border mb-2"
            >
              <div class="d-flex align-items-center mb-1">
                <span class="text-xs font-bold text-dark w-16">GWAS</span>
                <span class="text-xs text-primary font-mono text-truncate">{{ item.gwasFileName || item }}</span>
              </div>
              <div v-if="item.ldFileName" class="d-flex align-items-center">
                <span class="text-xs font-bold text-dark w-16">LD Ref</span>
                <span class="text-xs text-muted font-mono text-truncate">{{ item.ldFileName }}</span>
              </div>
            </div>
          </div>

          <div>
            <label class="text-2xs text-uppercase font-bold text-muted mb-3 d-block tracking-wider">Analysis Parameters</label>
            <div class="row g-3">
              <div v-for="(p, index) in (showDetail.parameterEnterDTOS || [])" :key="index" class="col-6">
                <div class="p-2 border-bottom">
                  <div class="text-2xs text-muted text-uppercase mb-1">{{ p.parameterName }}</div>
                  <div class="text-xs font-black text-dark text-truncate" :title="p.parameterValue">{{ p.parameterValue }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="px-8 py-4 bg-light text-end">
          <button class="btn btn-sm btn-neutral rounded-pill px-6" @click="hideModal">Close</button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { isEmpty } from "@/utils/validate";
import { listJobs, getJobDetail, getDownloadStream, cancelJob, purgeJobRecord } from "@/api/job";

export default {
  name: "StatisticsItem",

  data() {
    return {
      files: { total: 0, fileList: [] },
      loading: false,
      showDetail: {},
      total: 0,
      pageSize: 10,
      currentPage: 1,
      keyword: "",
      timer: null,
    };
  },

  computed: {
    runningCount() {
      return this.files.fileList.filter(f => ["Started", "Submitted"].includes(f.status)).length;
    },
    finishedCount() {
      // 注意：这里由于分页原因，只能统计当前页。如需统计全局，建议由后端返回统计字段。
      return this.files.fileList.filter(f => f.status === "Finished").length;
    }
  },

  methods: {
    mapStatusToUI(s) {
      const x = String(s || "").toLowerCase();
      if (x === "queued" || x === "draft") return { s: "Submitted", c: "bg-secondary" };
      if (x === "running" || x === "submitted" || x === "started") return { s: "Started", c: "bg-warning" };
      if (x === "finished" || x === "succeeded" || x === "success") return { s: "Finished", c: "bg-success" };
      if (x === "failed" || x === "error") return { s: "Failed", c: "bg-danger" };
      if (x === "stopped" || x === "canceled" || x === "cancelled") return { s: "Stopped", c: "bg-secondary" };
      return { s: s || "--", c: "bg-secondary" };
    },
    formatDuration(sec) {
  if (sec === null || sec === undefined || isNaN(sec)) return "";

  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;

  if (h > 0) return `${h}h ${m}m ${r}s`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
},

    normalizeJob(job) {
  const info = this.mapStatusToUI(job.status);

  const algoName = job.expand?.algorithm?.name || job.algorithm_name || job.job_name || "Unknown Method";

  let params = job.parameterEnterDTOS || [];
  if (!params.length && job.params) {
    params = Object.keys(job.params).map(k => ({
      parameterName: k,
      parameterValue: String(job.params[k])
    }));
  }

  const submittedAt = job.submitted_at || job.submittedAt || job.created || null;
  const finishedAt = job.finished_at || job.finishedAt || job.completed_at || null;


// duration 秒：Finished/Failed 用 finishedAt；Running 用 now
let durationSec = null;
if (submittedAt) {
  const start = new Date(submittedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  durationSec = Math.max(0, Math.floor((end - start) / 1000));
}

const durationText = this.formatDuration(durationSec);
  return {
    uuid: job.id,
    name: job.job_name || `Job #${job.id}`,
    algorithmsName: algoName,

    submittedAt,
    finishedAt,
    durationText,
    // durationSec,
    // 仍然保留你原来字段，避免其他地方依赖
    uploadDate: submittedAt ? new Date(submittedAt).toLocaleString() : "--",

    ranking: (job.queue_pos || job.queue_pos === 0) ? String(job.queue_pos) : "--",
    status: info.s,
    colorClass: info.c,

    parameterEnterDTOS: params,
    gwasAndLDFilenames: job.gwasAndLDFilenames || [],
    _raw: job,
  };
},

    async init(page, size) {
      this.loading = true;
      try {
        const query = {
          page: page || this.currentPage,
          perPage: size || this.pageSize,
          keyword: this.keyword
        };

        const res = await listJobs(query);
        
        // PocketBase listJobs 直接返回 { items, totalItems, ... }
        this.total = res.totalItems;
        this.currentPage = res.page;
        this.pageSize = res.perPage;

        this.files.fileList = (res.items || []).map(this.normalizeJob);
        this.files.total = res.totalItems;

        // 动态控制轮询：如果有进行中的任务，则 10s 轮询一次；否则 30min 轮询一次
        const hasActive = this.files.fileList.some(x => ["Submitted", "Started"].includes(x.status));
        this.resetTimer(hasActive ? 10000 : 1800000);

      } catch (e) {
        console.error("[Jobs] Load Error:", e);
        this.$message?.error?.("Failed to synchronize with cluster status");
      } finally {
        this.loading = false;
      }
    },

    async setEnterDetail(job) {
      try {
        this.loading = true;
        const detail = await getJobDetail(job.uuid);
        this.showDetail = this.normalizeJob(detail);
        this.$modal.show("as-modal");
      } catch (e) {
        this.$message?.error?.("Could not retrieve job configuration");
      } finally {
        this.loading = false;
      }
    },

    hideModal() { this.$modal.hide("as-modal"); },

    async downloadResult(jobId) {
      this.loading = true;
      try {
        const { blob, filename } = await getDownloadStream(jobId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$message?.error?.(e.message || "Download failed");
      } finally {
        this.loading = false;
      }
    },
    prettyFormatRawLog(raw) {
  const lines = raw.split("\n");
  const output = [];

  for (let line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      output.push("");
      continue;
    }

    // 匹配: [timestamp] JSON
    const match = trimmed.match(/^\[(.+?)\]\s*(.+)$/);

    if (match) {
      const ts = match[1];
      let body = match[2];

      // 去掉外层多余引号
      body = body.replace(/^"+|"+$/g, "");

      try {
        const parsed = JSON.parse(body);
        const pretty = JSON.stringify(parsed, null, 2);

        output.push(`[${ts}]`);
        output.push(pretty);
      } catch {
        output.push(trimmed);
      }
    } else {
      // 尝试整体 JSON
      try {
        const parsed = JSON.parse(trimmed);
        output.push(JSON.stringify(parsed, null, 2));
      } catch {
        output.push(trimmed);
      }
    }
  }

  return output.join("\n");
},
    async openLogs(jobId) {
  try {
    const data = await getJobDetail(jobId);

    let raw = data.log || data.error_log || data.cromwell_error_text || data.message || "";
    raw = String(raw || "");

    // 1️⃣ 去掉开头 "null\n"
    raw = raw.replace(/^null\s*\n?/i, "");

    // 2️⃣ 把 \N 和 \n 转为真实换行
    raw = raw
      .replace(/\\N/g, "\n")
      .replace(/\\n/g, "\n");

    // 3️⃣ 尝试逐行解析 JSON 并美化
    const formatted = this.prettyFormatRawLog(raw);

    const html = `
      <div style="max-height:65vh;overflow:auto;padding:14px;">
        <pre style="
          white-space:pre-wrap;
          word-break:break-word;
          font-size:12px;
          line-height:1.5;
          background:#f8fafc;
          border:1px solid #e5e7eb;
          border-radius:10px;
          padding:16px;
          margin:0;
        ">${this.escapeHtml(formatted)}</pre>
      </div>
    `;

    if (this.$alert) {
      this.$alert(html, `System Logs - ${jobId}`, {
        dangerouslyUseHTMLString: true,
        confirmButtonText: "Close",
        customClass: "log-modal"
      });
    } else {
      alert(formatted);
    }

  } catch (e) {
    console.error(e);
    this.$message?.error?.("Failed to fetch logs");
  }
},

escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
},

    async abortRunner(jobId) {
      try {
        await cancelJob(jobId);
        this.$message.success("Termination request sent");
        this.init();
      } catch (e) {
        this.$message.error("Failed to stop job");
      }
    },

    async deleteRunner(jobId) {
      this.$MessageBox.confirm("Are you sure you want to permanently delete this record?", "Warning", {
        confirmButtonText: "Delete",
        cancelButtonText: "Keep it",
        type: "warning"
      }).then(async () => {
        try {
          await purgeJobRecord(jobId);
          this.$message.success("Record deleted");
          this.init();
        } catch (e) {
          this.$message.error("Delete failed");
        }
      }).catch(() => {});
    },

    changePage(val) {
      this.init(val, this.pageSize);
    },

    handleSizeChange(val) {
      this.pageSize = val;
      this.init(1, val);
    },

    resetTimer(interval) {
      if (this.timer) clearInterval(this.timer);
      this.timer = setInterval(() => this.init(this.currentPage, this.pageSize), interval);
    }
  },

  mounted() {
    this.init();
  },

  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>

<style scoped>
.pulse-warning { animation: pulse-warning 2s infinite; }
@keyframes pulse-warning {
  0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(245, 158, 11, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
}

.pulse-active i { position: relative; }
.pulse-active i::after {
  content: '';
  position: absolute;
  width: 100%; height: 100%;
  top: 0; left: 0;
  background: inherit;
  border-radius: inherit;
  animation: pulse-warning 2s infinite;
}

.w-16 { width: 4rem; flex-shrink: 0; }
.cursor-pointer { cursor: pointer; }
.font-mono { font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace; }

::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #5c60f5 !important;
}

.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
}
.pulse-duration { animation: pulse-duration 1.6s infinite; }
@keyframes pulse-duration {
  0% { opacity: 1; }
  50% { opacity: 0.65; }
  100% { opacity: 1; }
}
.pulse-color {
  animation: pulse-color 1.6s infinite;
}

@keyframes pulse-color {
  0%   { background-color: #eef2ff; }
  50%  { background-color: #dbeafe; }
  100% { background-color: #eef2ff; }
}
</style>